import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import campaignContext from '../../services/campaignContext';

/**
 * Rutas donde NO se reescribe la URL.
 *
 * - `/admin`: el panel no forma parte de ninguna campaña y, si el propio
 *   administrador entra por un enlace de campaña, sus paseos por el panel
 *   ensucian las sesiones del informe.
 * - `/payment-result`: ePayco devuelve al usuario con sus propios parámetros;
 *   acá no se toca nada.
 * - `/reset-password` y `/verify-email`: llevan un token en la URL y son
 *   enlaces que la gente reenvía. No se les añade ruido.
 */
const RUTAS_EXCLUIDAS = [
  /^\/admin(\/|$)/,
  /^\/payment-result(\/|$)/,
  /^\/reset-password(\/|$)/,
  /^\/verify-email(\/|$)/,
];

export const estaExcluida = pathname => RUTAS_EXCLUIDAS.some(r => r.test(pathname));

/**
 * Devuelve la query string con los parámetros de campaña añadidos, o null si no
 * había nada que añadir.
 *
 * FUSIONA, nunca pisa: si la página ya trae un parámetro con ese nombre gana el
 * suyo. Así `/productos?search=catan` sigue funcionando con la campaña activa.
 * Devolver null cuando no cambia nada es lo que corta el bucle de renderizado:
 * sin eso, cada navegación provocaría otra navegación.
 */
export const fusionarParams = (search, pares) => {
  const params = new URLSearchParams(search);
  let cambio = false;
  pares.forEach(([clave, valor]) => {
    if (!params.has(clave)) {
      params.set(clave, valor);
      cambio = true;
    }
  });
  return cambio ? `?${params.toString()}` : null;
};

/**
 * Mantiene los parámetros de campaña en la URL al navegar dentro del sitio.
 *
 * Va junto a <AnalyticsTracker/> dentro del Router porque es el ÚNICO punto por
 * el que pasan todas las navegaciones: el proyecto tiene 46 <Link> repartidos en
 * 20 archivos y 14 componentes con useNavigate, sin ningún envoltorio común.
 * Interceptar acá evita tocarlos todos y evita que el próximo enlace que alguien
 * escriba se olvide de arrastrar la campaña.
 *
 * El objetivo es que el enlace siga siendo compartible y recargable: la
 * atribución en sí ya sobrevive en el almacén del navegador (ver
 * campaignContext), pero una URL sin parámetros que alguien copia y pega en otro
 * dispositivo pierde el rastro.
 */
export default function CampaignParamsKeeper() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Reconoce un clic de campaña aunque llegue en una navegación interna.
    // Es idempotente: si es la misma campaña, conserva el click_id original.
    const ctx = campaignContext.capture(location.search);
    if (!ctx || estaExcluida(location.pathname)) return;

    const search = fusionarParams(location.search, campaignContext.toParams(ctx));
    if (!search) return;

    // `replace` para no llenar el historial de entradas duplicadas, y `state`
    // explícito porque navigate() lo descarta si no se lo pasan: ahí viaja el
    // origen ({ source }) que la ficha de producto usa para saber desde dónde
    // se llegó, y perderlo dejaría todas las visitas como 'desconocido'.
    // `preventScrollReset` porque si no, el router manda el scroll arriba al
    // reescribir: en una ficha larga con conexión lenta el usuario ya bajó
    // cuando corre este efecto, y vería un salto sin haber tocado nada.
    // El `hash` se pasa explícito porque navigate() lo descarta en silencio.
    navigate(
      { pathname: location.pathname, search, hash: location.hash },
      { replace: true, state: location.state, preventScrollReset: true }
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  return null;
}
